import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { api } from "@/lib/api";
import { fmtDate } from "@/lib/site";

const EMPTY = { title: "", slug: "", category: "News", image_url: "", excerpt: "", content: "", published: true };

const autoSlug = (title) =>
  title
    .toLowerCase()
    .replace(/['’]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

export default function AdminBlog() {
  const [posts, setPosts] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [saving, setSaving] = useState(false);
  const [slugTouched, setSlugTouched] = useState(false);

  const load = () => {
    api
      .get("/admin/blog")
      .then((res) => setPosts(res.data))
      .catch(() => {
        setPosts([]);
        toast.error("Couldn't load posts");
      });
  };
  useEffect(load, []);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY);
    setSlugTouched(false);
    setOpen(true);
  };

  const openEdit = (post) => {
    setEditing(post);
    setForm({
      title: post.title,
      slug: post.slug,
      category: post.category,
      image_url: post.image_url || "",
      excerpt: post.excerpt || "",
      content: post.content || "",
      published: post.published,
    });
    setSlugTouched(true);
    setOpen(true);
  };

  const setField = (key, value) => {
    setForm((f) => {
      const next = { ...f, [key]: value };
      if (key === "title" && !slugTouched && !editing) next.slug = autoSlug(value);
      return next;
    });
  };

  const save = async () => {
    if (!form.title.trim()) {
      toast.error("Please add a title");
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/blog/${editing.id}`, form);
        toast.success("Post updated");
      } else {
        await api.post("/blog", form);
        toast.success("Post created");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong");
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/blog/${deleting.id}`);
      toast.success("Post deleted");
      setDeleting(null);
      load();
    } catch {
      toast.error("Couldn't delete post");
    }
  };

  return (
    <div data-testid="admin-blog-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold">Blog posts</h1>
          <p className="text-muted-foreground mt-1">Write, edit and publish news for parents and dancers.</p>
        </div>
        <Button onClick={openCreate} className="rounded-[4px] font-semibold w-fit" data-testid="admin-blog-create-button">
          <Plus className="h-4 w-4 mr-1.5" /> New post
        </Button>
      </div>

      <Card className="rounded-[4px] mt-6 overflow-hidden">
        <CardContent className="p-0">
          {posts === null ? (
            <div className="p-6 space-y-3">
              {[...Array(4)].map((_, i) => (
                <Skeleton key={i} className="h-14 rounded-[4px]" />
              ))}
            </div>
          ) : posts.length === 0 ? (
            <p className="p-10 text-center text-muted-foreground" data-testid="admin-blog-empty">
              No posts yet — write your first one!
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-secondary/50">
                  <TableHead>Title</TableHead>
                  <TableHead className="hidden md:table-cell">Category</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="hidden md:table-cell">Date</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {posts.map((p) => (
                  <TableRow key={p.id} data-testid="admin-blog-row">
                    <TableCell className="font-medium max-w-[280px]">
                      <p className="truncate">{p.title}</p>
                      <p className="text-xs text-muted-foreground truncate">/blog/{p.slug}</p>
                    </TableCell>
                    <TableCell className="hidden md:table-cell text-muted-foreground">{p.category}</TableCell>
                    <TableCell>
                      {p.published ? (
                        <Badge className="rounded-[4px] bg-[hsl(var(--kerrys-seafoam))]/25 text-[hsl(168,55%,25%)] border-0">Published</Badge>
                      ) : (
                        <Badge variant="outline" className="rounded-[4px]">Draft</Badge>
                      )}
                    </TableCell>
                    <TableCell className="hidden md:table-cell text-muted-foreground">{fmtDate(p.created_at)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1.5">
                        <Button variant="ghost" size="icon" onClick={() => openEdit(p)} aria-label={`Edit ${p.title}`} data-testid="admin-blog-edit-button">
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setDeleting(p)}
                          aria-label={`Delete ${p.title}`}
                          className="text-destructive hover:text-destructive"
                          data-testid="admin-blog-delete-button"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-card">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{editing ? "Edit post" : "New post"}</DialogTitle>
            <DialogDescription>Posts marked as published appear on the site immediately.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="post-title">Title</Label>
              <Input id="post-title" value={form.title} onChange={(e) => setField("title", e.target.value)} data-testid="admin-blog-title-input" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="post-slug">Slug (URL)</Label>
                <Input
                  id="post-slug"
                  value={form.slug}
                  onChange={(e) => {
                    setSlugTouched(true);
                    setField("slug", e.target.value);
                  }}
                  data-testid="admin-blog-slug-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="post-category">Category</Label>
                <Input id="post-category" value={form.category} onChange={(e) => setField("category", e.target.value)} data-testid="admin-blog-category-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="post-image">Featured image URL (optional)</Label>
              <Input id="post-image" value={form.image_url} onChange={(e) => setField("image_url", e.target.value)} placeholder="https://…" data-testid="admin-blog-image-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="post-excerpt">Excerpt (short summary)</Label>
              <Textarea id="post-excerpt" rows={2} value={form.excerpt} onChange={(e) => setField("excerpt", e.target.value)} data-testid="admin-blog-excerpt-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="post-content">Content</Label>
              <p className="text-xs text-muted-foreground">Separate paragraphs with a blank line. Start a line with “## ” for a subheading.</p>
              <Textarea id="post-content" rows={10} value={form.content} onChange={(e) => setField("content", e.target.value)} data-testid="admin-blog-content-input" />
            </div>
            <div className="flex items-center gap-3">
              <Switch id="post-published" checked={form.published} onCheckedChange={(v) => setField("published", v)} data-testid="admin-blog-published-switch" />
              <Label htmlFor="post-published">{form.published ? "Published" : "Draft"}</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-[4px]">
              Cancel
            </Button>
            <Button onClick={save} disabled={saving} className="rounded-[4px] font-semibold" data-testid="admin-blog-save-button">
              {saving ? "Saving…" : editing ? "Save changes" : "Create post"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleting} onOpenChange={(v) => !v && setDeleting(null)}>
        <AlertDialogContent className="bg-card">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this post?</AlertDialogTitle>
            <AlertDialogDescription>
              “{deleting?.title}” will be permanently removed. This can’t be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-[4px]">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="rounded-[4px] bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="admin-blog-confirm-delete-button">
              Delete post
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
